import React, { useEffect, useState } from 'react';
import { BadgeCheck, Save } from 'lucide-react';
import IscrizioniAlbo from './IscrizioniAlbo';
import api from '../../services/api';
import ErroreLettura from '../comuni/ErroreLettura';
import { conUnTentativo, motivoDellaLettura } from '../../utils/primaLettura';
import { useSporco, useUscitaProtetta } from '../../utils/uscitaProtetta';
import '../../styles/MedicoForm.css';

/**
 * MiaIscrizione — il riquadro «La mia iscrizione all'albo» in «Il mio
 * account». Il medico compila la PROPRIA, e il server lo verifica sulla
 * sessione: qui non passa nessun id di medico. Stesso editor della
 * scheda del medico (IscrizioniAlbo): albo -> sede guidata, numero,
 * sezione, data, principale.
 */
const spoglia = (l) => l.map((i) => ({
    albo_codice: i.albo_codice || '', sede_id: i.sede_id || '', numero: i.numero || '',
    sezione: i.sezione || '', data_iscrizione: i.data_iscrizione ? String(i.data_iscrizione).slice(0, 10) : '',
    principale: !!i.principale,
}));

const MiaIscrizione = () => {
    const [iscrizioni, setIscrizioni] = useState([]);
    const [salvate, setSalvate] = useState([]);
    const [versione, setVersione] = useState(0);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [esito, setEsito] = useState(null);
    const sporco = useSporco(iscrizioni, versione);
    useUscitaProtetta(sporco);

    /* La lettura riprova da sola una volta, poi il motivo con «Riprova»; il
       suo errore sta per conto suo (primaLettura.js). */
    const [erroreLettura, setErroreLettura] = useState(null);
    const carica = () => {
        conUnTentativo(() => api.get('/medici/mie-iscrizioni'))
            .then((r) => {
                const l = r.data?.data?.iscrizioni || [];
                setIscrizioni(spoglia(l)); setSalvate(l); setVersione((v) => v + 1);
                setErroreLettura(null);
            })
            .catch((e) => setErroreLettura(motivoDellaLettura(e, 'Non riesco a leggere la tua iscrizione')));
    };
    useEffect(() => { carica(); }, []);

    const salva = async () => {
        setInCorso(true); setErrore(null); setEsito(null);
        try {
            const r = await api.put('/medici/mie-iscrizioni', { iscrizioni });
            const l = r.data?.data?.iscrizioni || [];
            setIscrizioni(spoglia(l)); setSalvate(l); setVersione((v) => v + 1);
            setEsito(r.data?.message || 'Salvato.');
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Errore nel salvataggio');
        } finally {
            setInCorso(false);
        }
    };

    const principale = salvate.find((i) => i.principale) || salvate[0];

    return (
        <section className="prof-riquadro">
            <h2 className="prof-titolo"><BadgeCheck size={18} aria-hidden="true" /> La mia iscrizione all'albo</h2>
            <p className="prof-spiega">
                Sui referti e sulle ricette si stampa «Iscritto all'<i>albo</i> <i>sede</i> n. <i>numero</i>»,
                dall'iscrizione principale. Un numero senza albo e sede non identifica nessuno: gli albi
                sono territoriali e il numero si ripete. Se manca qualcosa, concludere un referto chiede
                una conferma. Puoi cambiare solo la tua.
            </p>
            {principale && (
                <p className={`prof-stato ${principale.completa ? '' : 'prof-manca'}`}>
                    {principale.completa
                        ? <>Oggi si stampa: <b>{principale.riga}</b></>
                        : <>Oggi <b>non si stampa niente</b>: manca {principale.mancanti.join(', ')}.</>}
                </p>
            )}
            <ErroreLettura testo={erroreLettura} onRiprova={carica} classe="prof-errore" />
            {errore && <p className="prof-errore">{errore}</p>}
            {esito && <p className="prof-esito">{esito}</p>}
            <IscrizioniAlbo valore={iscrizioni} onChange={setIscrizioni} disabilitato={inCorso} />
            <div className="prof-azioni">
                <button className="prof-btn" onClick={salva} disabled={inCorso || !sporco}>
                    <Save size={15} /> {inCorso ? 'Salvo…' : 'Salva la mia iscrizione'}
                </button>
            </div>
        </section>
    );
};

export default MiaIscrizione;
